import React from 'react'
import AdminSidebar from "../Sidebar/AdminSidebar";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { SuperAdminApis } from '../../apis/superAdminApi';

type WithdrawalStatus = 'PENDING' | 'SUCCESSFUL' | 'FAILED';

const STATUS_STYLES: Record<string, string> = {
  PENDING:    'bg-yellow-100 text-yellow-800',
  SUCCESSFUL: 'bg-green-100  text-green-800',
  FAILED:     'bg-red-100    text-red-800',
};

const getPageFromLink = (label: string, url: string | null): string => {
  if (!isNaN(Number(label))) return label;
  if (url) {
    try { return new URL(url).searchParams.get('page') || '1'; }
    catch { return '1'; }
  }
  return '1';
};

function WitdrawalRequest() {
  const [withdrawals, setWithdrawals]   = React.useState<any>({});
  const [searchText, setSearchText]     = React.useState('');
  const [statusFilter, setStatusFilter] = React.useState<string>('');
  const [loading, setLoading]           = React.useState(false);

  const [modalOpen, setModalOpen]         = React.useState(false);
  const [selectedRecord, setSelectedRecord] = React.useState<any>(null);
  const [processing, setProcessing]       = React.useState(false);

  const fetchWithdrawals = React.useCallback((page = '') => {
    setLoading(true);
    SuperAdminApis.getAllWitdrawals(page, { search: searchText }).then(
      (res: any) => {
        if (res?.data) setWithdrawals(res.data?.data ?? res.data);
        setLoading(false);
      }
    ).catch(() => setLoading(false));
  }, [searchText]);

  React.useEffect(() => { fetchWithdrawals(); }, []);

  const handleSearch = () => fetchWithdrawals('');

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch();
  };

  const openModal = (record: any) => {
    setSelectedRecord(record);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedRecord(null);
  };

  const processWithdrawal = () => {
    if (!selectedRecord) return;
    setProcessing(true);

    const formData = new FormData();
    Object.entries(selectedRecord).forEach(([key, val]) => {
      formData.append(key, String(val));
    });

    SuperAdminApis.payOutCustomers(formData).then(
      (res: any) => {
        if (res?.data) {
          toast.success('Transfer initiated successfully');
          fetchWithdrawals('');
        } else {
          toast.error(res?.response?.data?.message || 'Transfer failed');
        }
        setProcessing(false);
        closeModal();
      }
    ).catch((err: any) => {
      toast.error(err?.response?.data?.message || 'Transfer failed');
      setProcessing(false);
      closeModal();
    });
  };

  const allRecords: any[] = withdrawals?.data ?? [];
  const filtered = statusFilter
    ? allRecords.filter((r: any) => r.status === statusFilter)
    : allRecords;

  const counts = {
    PENDING:    allRecords.filter(r => r.status === 'PENDING').length,
    SUCCESSFUL: allRecords.filter(r => r.status === 'SUCCESSFUL').length,
    FAILED:     allRecords.filter(r => r.status === 'FAILED').length,
  };

  return (
    <>
      <AdminSidebar />
      <ToastContainer position="top-right" autoClose={3000} />

      <div className="relative md:ml-64 bg-gray-50 min-h-screen">
        <div className="py-8 lg:py-12 lg:px-10 px-6">

          {/* Header */}
          <div className="flex flex-wrap items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-800">Withdrawal Requests</h1>
              <p className="text-sm text-gray-500 mt-1">Review and process vendor withdrawal requests</p>
            </div>
          </div>

          {/* Stat chips */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            {(['PENDING', 'SUCCESSFUL', 'FAILED'] as WithdrawalStatus[]).map(s => (
              <button
                key={s}
                onClick={() => setStatusFilter(statusFilter === s ? '' : s)}
                className={`rounded-lg p-4 text-left border-2 transition-all ${
                  statusFilter === s ? 'border-[#0071BC] shadow-md' : 'border-transparent'
                } ${
                  s === 'PENDING'    ? 'bg-yellow-50' :
                  s === 'SUCCESSFUL' ? 'bg-green-50'  : 'bg-red-50'
                }`}
              >
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{s}</p>
                <p className="text-2xl font-bold text-gray-800 mt-1">{counts[s]}</p>
              </button>
            ))}
          </div>

          {/* Search + filter bar */}
          <div className="bg-white rounded-lg shadow-sm p-4 mb-4 flex flex-wrap gap-3 items-center justify-between">
            <div className="flex gap-3 items-center flex-wrap">
              <div className="relative">
                <svg className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16">
                  <path fill="currentColor" d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0a5.5 5.5 0 0 1 11 0z"/>
                </svg>
                <input
                  type="text"
                  value={searchText}
                  onChange={e => setSearchText(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Search by email or reference..."
                  className="pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-600 w-72 focus:outline-none focus:ring-2 focus:ring-[#0071BC]"
                />
              </div>

              <select
                value={statusFilter}
                onChange={e => setStatusFilter(e.target.value)}
                className="border border-gray-300 text-gray-600 text-sm rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#0071BC]"
              >
                <option value="">All Statuses</option>
                <option value="PENDING">Pending</option>
                <option value="SUCCESSFUL">Successful</option>
                <option value="FAILED">Failed</option>
              </select>

              <button
                onClick={handleSearch}
                disabled={loading}
                className="bg-[#0071BC] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
              >
                {loading ? 'Loading…' : 'Search'}
              </button>
            </div>

            <span className="text-sm text-gray-500">{filtered.length} record{filtered.length !== 1 ? 's' : ''}</span>
          </div>

          {/* Table */}
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left text-gray-600">
                <thead className="text-xs uppercase bg-gray-50 text-gray-500 border-b">
                  <tr>
                    <th className="px-6 py-4">#</th>
                    <th className="px-6 py-4">Recipient</th>
                    <th className="px-6 py-4">Bank Details</th>
                    <th className="px-6 py-4">Amount</th>
                    <th className="px-6 py-4">Reference</th>
                    <th className="px-6 py-4">Status</th>
                    <th className="px-6 py-4">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={7} className="px-6 py-16 text-center text-gray-400">
                        <div className="flex justify-center items-center gap-2">
                          <div className="w-5 h-5 border-2 border-[#0071BC] border-t-transparent rounded-full animate-spin"></div>
                          Loading withdrawals…
                        </div>
                      </td>
                    </tr>
                  ) : filtered.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-6 py-16 text-center text-gray-400">
                        <div className="flex flex-col items-center gap-2">
                          <svg xmlns="http://www.w3.org/2000/svg" className="w-10 h-10 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 14l6-6m-5.5.5h.01m4.99 5h.01M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16l3.5-2 3.5 2 3.5-2 3.5 2z"/>
                          </svg>
                          <p className="font-medium">No withdrawal requests found</p>
                          {statusFilter && <p className="text-xs">Try clearing the status filter</p>}
                        </div>
                      </td>
                    </tr>
                  ) : (
                    filtered.map((record: any, idx: number) => (
                      <tr key={record.id ?? idx} className="border-b hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4 text-gray-400">{idx + 1}</td>
                        <td className="px-6 py-4">
                          <div className="font-medium text-gray-800">{record.first_name} {record.last_name}</div>
                          <div className="text-xs text-gray-400">{record.email}</div>
                          <div className="text-xs text-gray-400">{record.mobile_number}</div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="font-medium text-gray-800">{record.account_bank}</div>
                          <div className="text-xs text-gray-500 font-mono">{record.account_number}</div>
                        </td>
                        <td className="px-6 py-4">
                          <span className="font-semibold text-gray-800">₦{Number(record.amount).toLocaleString()}</span>
                        </td>
                        <td className="px-6 py-4">
                          <span className="font-mono text-xs text-gray-500">{record.reference}</span>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${STATUS_STYLES[record.status] ?? 'bg-gray-100 text-gray-800'}`}>
                            {record.status}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          {record.status === 'PENDING' ? (
                            <button
                              onClick={() => openModal(record)}
                              className="bg-[#0071BC] text-white text-xs font-medium px-3 py-1.5 rounded-lg hover:bg-blue-700 transition-colors"
                            >
                              Process
                            </button>
                          ) : (
                            <span className="text-xs text-gray-400 italic">
                              {record.status === 'SUCCESSFUL' ? 'Completed' : 'Failed'}
                            </span>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {withdrawals?.links && withdrawals.links.length > 3 && (
              <div className="px-6 py-4 border-t flex items-center gap-1">
                {withdrawals.links.map((link: any, idx: number) => (
                  <button
                    key={idx}
                    onClick={() => fetchWithdrawals(getPageFromLink(link.label, link.url))}
                    disabled={!link.url || link.active || loading}
                    className={`px-3 py-1.5 text-xs rounded-lg font-medium transition-colors ${
                      link.active
                        ? 'bg-[#0071BC] text-white'
                        : link.url
                          ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          : 'bg-gray-50 text-gray-300 cursor-not-allowed'
                    }`}
                    dangerouslySetInnerHTML={{ __html: link.label }}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Confirm modal */}
      {modalOpen && selectedRecord && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-gray-800">Confirm Transfer</h2>
              <button onClick={closeModal} className="text-gray-400 hover:text-gray-600 text-xl font-bold">&times;</button>
            </div>

            <div className="bg-gray-50 rounded-lg p-4 space-y-2 mb-6 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Recipient</span>
                <span className="font-medium text-gray-800">{selectedRecord.first_name} {selectedRecord.last_name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Email</span>
                <span className="text-gray-700">{selectedRecord.email}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Bank</span>
                <span className="font-medium text-gray-800">{selectedRecord.account_bank}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Account No.</span>
                <span className="font-mono text-gray-700">{selectedRecord.account_number}</span>
              </div>
              <div className="flex justify-between border-t pt-2 mt-2">
                <span className="text-gray-500 font-medium">Amount</span>
                <span className="text-lg font-bold text-[#0071BC]">₦{Number(selectedRecord.amount).toLocaleString()}</span>
              </div>
            </div>

            <p className="text-sm text-gray-500 mb-6">
              This action will initiate a bank transfer. Once processed it cannot be reversed.
            </p>

            <div className="flex gap-3">
              <button
                onClick={closeModal}
                disabled={processing}
                className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-gray-700 text-sm font-medium hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={processWithdrawal}
                disabled={processing}
                className="flex-1 px-4 py-2.5 bg-[#0071BC] text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {processing && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>}
                {processing ? 'Processing…' : 'Confirm Transfer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default WitdrawalRequest;
